import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@/App.css';
import GlobalTooltip from '@/components/GlobalTooltip.jsx';
import HomePage from './pages/HomePage.jsx';
import SolutionsPage from './pages/SolutionsPage.jsx';
import PricingPage from './pages/PricingPage.jsx';
import SignInPage from './pages/SignInPage.jsx';
import SignUpPage from './pages/SignUpPage.jsx';
import ForgotPasswordPage from './pages/ForgotPasswordPage.jsx';
import FirstLoginPasswordPage from './pages/FirstLoginPasswordPage.jsx';
import RequestDemoPage from './pages/RequestDemoPage.jsx';
import ContactUsPage from './pages/ContactUsPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import SuperAdminDashboard from './pages/SuperAdminDashboard.jsx';
import HospitalsPage from './pages/HospitalsPage.jsx';
import DemoPage from './pages/DemoPage.jsx';
import CRMDashboard from './pages/CRMDashboard.jsx';
import AddLeadPage from './pages/AddLeadPage.jsx';
import LeadDetailPage from './pages/LeadDetailPage.jsx';
import ManageAssociatesPage from './pages/ManageAssociatesPage.jsx';
import ManageOfficersPage from './pages/ManageOfficersPage.jsx';
import DemoRequestsPage from './pages/DemoRequestsPage.jsx';
import ContactSubmissionsPage from './pages/ContactSubmissionsPage.jsx';
import TrainingContentPage from './pages/TrainingContentPage.jsx';
import AddTrainingContentPage from './pages/AddTrainingContentPage.jsx';
import ViewTrainingContentPage from './pages/ViewTrainingContentPage.jsx';
import AssessmentsPage from './pages/AssessmentsPage.jsx';
import FeedbackManagementPage from './pages/FeedbackManagementPageNew.jsx';
import QuestionBankPage from './pages/QuestionBankPage.jsx';
import QuestionBankPageNew from './pages/QuestionBankPageNew.jsx';
import QuestionBankPageUnified from './pages/QuestionBankPageUnified.jsx';
import ManageQuestionnairesPage from './pages/ManageQuestionnairesPage.jsx';
import CreateQuestionnairePage from './pages/CreateQuestionnairePage.jsx';
import TagToTopicsPage from './pages/TagToTopicsPage.jsx';
import AssessmentAnalyticsPage from './pages/AssessmentAnalyticsPage.jsx';
import PreAssessmentNotesEditorPage from './pages/PreAssessmentNotesEditorPage.jsx';
import PreAssessmentNotesViewPage from './pages/PreAssessmentNotesViewPage.jsx';
import TakeAssessmentPage from './pages/TakeAssessmentPage.jsx';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage.jsx';
import TermsConditionsPage from './pages/TermsConditionsPage.jsx';
import ProfilePage from './pages/ProfilePage.jsx';
import SubscriptionDashboardPage from './pages/SubscriptionDashboardPage.jsx';
import PlansPage from './pages/PlansPage.jsx';
import PaymentPage from './pages/PaymentPage.jsx';
import InvoicesPage from './pages/InvoicesPage.jsx';
import PlanDetailsPage from './pages/PlanDetailsPage.jsx';
import RenewPage from './pages/RenewPage.jsx';
import ManageSubscriptionsPage from './pages/ManageSubscriptionsPage.jsx';
import AddSubscriptionPage from './pages/AddSubscriptionPage.jsx';
import EditSubscriptionPage from './pages/EditSubscriptionPage.jsx';
import SelfConfigurationPage from './pages/SelfConfigurationPage.jsx';
import HospitalSetupPage from './pages/HospitalSetupPage.jsx';
import StaffManagementPage from './pages/StaffManagementPage.jsx';
import OfflineAttendancePage from './pages/OfflineAttendancePage.jsx';
import ReportsDashboardPage from './pages/ReportsDashboardPage.jsx';
// import AuditCompliancePage from './pages/AuditCompliancePage.jsx'; // Moved to Reports Dashboard
import EvaluationDashboardPageEnhanced from './pages/EvaluationDashboardPageEnhanced.jsx';
import HospitalEvaluationDashboard from './pages/HospitalEvaluationDashboard.jsx';
import EvaluationTriggerSimplified from './pages/EvaluationTriggerSimplified.jsx';
import ManagerEvaluationDashboard from './pages/ManagerEvaluationDashboard.jsx';
import ManagerEvaluationFormPage from './pages/ManagerEvaluationFormPage.jsx';
import StaffAccountsPage from './pages/StaffAccountsPage.jsx';
import TopicAssignmentPage from './pages/TopicAssignmentPage.jsx';
import LeaderboardConfigPage from './pages/LeaderboardConfigPage.jsx';
import StaffDashboardPage from './pages/StaffDashboardPage.jsx';
import LeaderboardPage from './pages/LeaderboardPage.jsx';
import StaffProfilePage from './pages/StaffProfilePage.jsx';
import ReportingManagerDashboard from './pages/ReportingManagerDashboard.jsx';
import ReportingManagerLandingPage from './pages/ReportingManagerLandingPage.jsx';
import EmailTemplatesPage from './pages/EmailTemplatesPage.jsx';
import EmailTemplateFormPage from './pages/EmailTemplateFormPage.jsx';
import DesignSystemPage from './pages/DesignSystemPage.jsx';
import CMSSettingsPage from './pages/CMSSettingsPage.jsx';
import ExlvaAdminManagementPage from './pages/ExlvaAdminManagementPage.jsx';
import IntegrationSettingsPage from './pages/IntegrationSettingsPage.jsx';
import OnboardingSettingsPage from './pages/OnboardingSettingsPage.jsx';
import LocationManagementPage from './pages/LocationManagementPage.jsx';
import ViewHospitalPage from './pages/ViewHospitalPage.jsx';
import { NotificationProvider } from './components/NotificationModal';
import { BrandLogoProvider } from './contexts/BrandLogoContext';
import { BrandFaviconProvider } from './contexts/BrandFaviconContext';
import ScrollToTop from './components/ScrollToTop';
import InstallAppPrompt from './components/InstallAppPrompt';
import { getSessionReturnPath, rememberAuthenticatedPath, restoreStoredSession } from './utils/authSession';

function AuthPathMemory() {
  const location = useLocation();

  useEffect(() => {
    if (localStorage.getItem('token')) {
      rememberAuthenticatedPath(location.pathname, location.search);
    }
  }, [location.pathname, location.search]);

  return null;
}

function AuthAwarePublicRoute({ children }) {
  const [redirectPath, setRedirectPath] = useState(null);
  const [checking, setChecking] = useState(() => Boolean(localStorage.getItem('token')));

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const user = await restoreStoredSession();
      if (cancelled) return;
      if (user) {
        setRedirectPath(getSessionReturnPath(user));
      }
      setChecking(false);
    };

    if (localStorage.getItem('token')) {
      restore();
    } else {
      setChecking(false);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  if (redirectPath) return <Navigate to={redirectPath} replace />;
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return children;
}

function App() {
  return (
    <BrandLogoProvider>
    <BrandFaviconProvider>
    <NotificationProvider>
    <div className="App">
      <GlobalTooltip />
      <BrowserRouter>
        <ScrollToTop />
        <AuthPathMemory />
        <Routes>
          <Route path="/" element={<AuthAwarePublicRoute><HomePage /></AuthAwarePublicRoute>} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/first-login" element={<FirstLoginPasswordPage />} />
          <Route path="/request-demo" element={<RequestDemoPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/super-admin" element={<SuperAdminDashboard />} />
          <Route path="/hospitals" element={<HospitalsPage />} />
          <Route path="/view-hospital" element={<ViewHospitalPage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/crm" element={<CRMDashboard />} />
          <Route path="/crm/leads/add" element={<AddLeadPage />} />
          <Route path="/crm/leads/:leadId" element={<LeadDetailPage />} />
          <Route path="/crm/leads/:leadId/edit" element={<AddLeadPage />} />
          <Route path="/crm/associates" element={<ManageAssociatesPage />} />
          <Route path="/crm/officers" element={<ManageOfficersPage />} />
          <Route path="/demo-requests" element={<DemoRequestsPage />} />
          <Route path="/contact-submissions" element={<ContactSubmissionsPage />} />
          <Route path="/training" element={<TrainingContentPage />} />
          <Route path="/training/add" element={<AddTrainingContentPage />} />
          <Route path="/training/:contentId" element={<ViewTrainingContentPage />} />
          <Route path="/training/:contentId/edit" element={<AddTrainingContentPage />} />
          
          {/* Assessments */}
          <Route path="/assessments" element={<AssessmentsPage />} />
          <Route path="/feedback" element={<FeedbackManagementPage />} />
          <Route path="/assessments/questions" element={<QuestionBankPage />} />
          <Route path="/assessments/manage" element={<ManageQuestionnairesPage />} />
          <Route path="/assessments/create-questionnaire" element={<CreateQuestionnairePage />} />
          <Route path="/assessments/questionnaire/:questionnaireId" element={<CreateQuestionnairePage />} />
          <Route path="/assessments/tag-to-topics" element={<TagToTopicsPage />} />
          <Route path="/assessments/analytics" element={<AssessmentAnalyticsPage />} />
          <Route path="/assessments/pre-assessment-notes" element={<PreAssessmentNotesEditorPage />} />
          <Route path="/pre-assessment-notes/:topicId" element={<PreAssessmentNotesViewPage />} />
          <Route path="/assessment/take/:questionnaireId" element={<TakeAssessmentPage />} />
          
          {/* Profile */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/subscription" element={<SubscriptionDashboardPage />} />
          <Route path="/subscription/plans" element={<PlansPage />} />
          <Route path="/subscription/payment" element={<PaymentPage />} />
          <Route path="/subscription/invoices" element={<InvoicesPage />} />
          <Route path="/subscription/plan-details" element={<PlanDetailsPage />} />
          <Route path="/subscription/renew" element={<RenewPage />} />
          <Route path="/subscriptions/manage" element={<ManageSubscriptionsPage />} />
          <Route path="/subscriptions/add" element={<AddSubscriptionPage />} />
          <Route path="/subscriptions/edit/:subscriptionId" element={<EditSubscriptionPage />} />
          <Route path="/hospital/self-configuration" element={<SelfConfigurationPage />} />
          <Route path="/hospital/setup" element={<HospitalSetupPage />} />
          <Route path="/hospital/staff-management" element={<StaffManagementPage />} />
          <Route path="/staff-management" element={<StaffManagementPage />} />
          <Route path="/evaluations" element={<HospitalEvaluationDashboard />} />
          <Route path="/evaluations/trigger" element={<EvaluationTriggerSimplified />} />
          <Route path="/hospital/evaluations" element={<HospitalEvaluationDashboard />} />
          <Route path="/manager/evaluations" element={<ManagerEvaluationDashboard />} />
          <Route path="/manager/evaluation-form" element={<ManagerEvaluationFormPage />} />
          <Route path="/hospital/attendance-upload" element={<OfflineAttendancePage />} />
          <Route path="/hospital/reports" element={<ReportsDashboardPage />} />
          {/* <Route path="/hospital/audit-compliance" element={<AuditCompliancePage />} /> Moved to Reports Dashboard Audit tab */}
          <Route path="/hospital/staff-accounts" element={<StaffAccountsPage />} />
          <Route path="/hospital/topic-assignment" element={<TopicAssignmentPage />} />
          <Route path="/hospital/leaderboard-config" element={<LeaderboardConfigPage />} />
          <Route path="/staff/dashboard" element={<StaffDashboardPage />} />
          <Route path="/staff/leaderboard" element={<LeaderboardPage />} />
          <Route path="/staff/profile" element={<StaffProfilePage />} />
          <Route path="/manager/landing" element={<ReportingManagerLandingPage />} />
          <Route path="/manager/dashboard" element={<ReportingManagerDashboard />} />
          <Route path="/admin/email-templates" element={<EmailTemplatesPage />} />
          <Route path="/admin/email-templates/create" element={<EmailTemplateFormPage />} />
          <Route path="/admin/email-templates/:templateId/edit" element={<EmailTemplateFormPage />} />
          <Route path="/admin/email-templates/:templateId/view" element={<EmailTemplateFormPage />} />
          <Route path="/admin/design-system" element={<DesignSystemPage />} />
          <Route path="/admin/cms-settings" element={<CMSSettingsPage />} />
          <Route path="/admin/exlva-admins" element={<ExlvaAdminManagementPage />} />
          <Route path="/admin/integration-settings" element={<IntegrationSettingsPage />} />
          <Route path="/admin/onboarding-settings" element={<OnboardingSettingsPage />} />
          <Route path="/admin/locations" element={<LocationManagementPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <InstallAppPrompt />
    </div>
    </NotificationProvider>
    </BrandFaviconProvider>
    </BrandLogoProvider>
  );
}

export default App;
